import { func, node } from 'prop-types';
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { assert, PageLoader } from '@kallidus/common';
import { getSuiteContext } from '@kallidus/common-ui-bloc';
import { useAsync, useAsyncFn } from 'react-use';
import getIsSignUpSite from '@ApplicationWide/getIsSignUpSite';
import { useHistory } from 'react-router-dom';
import { usePreAuthConfig } from './auth/usePreAuthConfig';

// from https://zeroheight.com/6822399ec/p/218128-colour/b/9521f6
export const DEFAULT_BRAND_COLOR = '#3F1DCB';

const APPLICATION_LOADING_SCREEN = null;

const APP_CONFIG_CONTEXT = createContext();

const LOGGED_IN_USER_CONTEXT = createContext();

const SUITE_CONTEXT = createContext();

export const useAppConfig = () => useContext(APP_CONFIG_CONTEXT);

export const useLoggedInUser = () => useContext(LOGGED_IN_USER_CONTEXT);

export const useSuiteContext = () => useContext(SUITE_CONTEXT);

export default function AppConfig({ fetchConfig, children, fetchAppConfig, onConfigLoaded, onBrandColorChange }) {
  const preAuthConfig = usePreAuthConfig();
  const [updatedConfig, setUpdatedConfig] = useState(undefined);
  const [loggedInUser, setLoggedInUser] = useState();
  const isSignUpSite = getIsSignUpSite();
  const history = useHistory();

  const {
    loading,
    error,
    value: { appConfig, suiteContext } = {},
  } = useAsync(async () => {
    try {
      if (!isSignUpSite) {
        const config = await fetchConfig(setLoggedInUser, preAuthConfig);
        let context;

        if (preAuthConfig.idsAuthenticationFeatureFlag) {
          try {
            context = await getSuiteContext((input, init) => fetch(input, init), {
              tenantFeaturesUrl: `${window.baseAppConfig.bffPath}/tenant/preview-features`,
              tenantConfigUrl: `${window.baseAppConfig.bffPath}/tenant/configurations`,
              suiteUserUrl: `${window.baseAppConfig.bffPath}/suite/user`,
            });
          } catch (e) {
            context = e?.data ?? {};
          }
        } else {
          context = {};
        }

        return { appConfig: config, suiteContext: context };
      }
    } finally {
      // This always needs to fire because it's used to remove the loading icon
      onConfigLoaded();
    }
  }, [isSignUpSite, fetchConfig, preAuthConfig, onConfigLoaded]);

  const handleRefresh = useCallback(async () => {
    const config = await fetchConfig(setLoggedInUser, preAuthConfig);
    setUpdatedConfig(config);
  }, [fetchConfig, preAuthConfig]);

  const [{ loading: userUpdating }, handleUpdateUser] = useAsyncFn(
    async (user, redirectTo) => {
      const config = await fetchAppConfig();
      setUpdatedConfig(config);
      setLoggedInUser(user);

      if (redirectTo) {
        history.replace(redirectTo);
      }
    },
    [fetchAppConfig, history],
  );

  const loggedInUserContextValue = useMemo(
    () => ({
      user: loggedInUser,
      refreshUser: handleUpdateUser,
      updateUser: setLoggedInUser,
    }),
    [loggedInUser, handleUpdateUser, setLoggedInUser],
  );

  const actualConfig = updatedConfig || appConfig;
  const appConfigContextValue = useMemo(
    () => ({
      ...actualConfig,
      refreshConfig: handleRefresh,
    }),
    [actualConfig, handleRefresh],
  );

  const { brandPrimaryColor } = getAttributesFromAppConfig(actualConfig);
  useEffect(() => {
    if (brandPrimaryColor) onBrandColorChange(brandPrimaryColor);
  }, [brandPrimaryColor, onBrandColorChange]);

  if (loading) {
    return APPLICATION_LOADING_SCREEN;
  }

  assert.ifError(error);

  return (
    <LOGGED_IN_USER_CONTEXT.Provider value={loggedInUserContextValue}>
      <APP_CONFIG_CONTEXT.Provider value={appConfigContextValue}>
        <SUITE_CONTEXT.Provider value={suiteContext}>{userUpdating ? <PageLoader /> : children}</SUITE_CONTEXT.Provider>
      </APP_CONFIG_CONTEXT.Provider>
    </LOGGED_IN_USER_CONTEXT.Provider>
  );
}

AppConfig.propTypes = {
  fetchConfig: func.isRequired,
  children: node,
  fetchAppConfig: func.isRequired,
  onConfigLoaded: func.isRequired,
  onBrandColorChange: func.isRequired,
};

function getLanguageFromConfig(config) {
  return config.preAuthConfig?.language || config.language;
}

function getAttributesFromAppConfig(appConfig) {
  const applicationName = appConfig?.preAuthConfig?.name || appConfig?.application?.name;
  const brandPrimaryColor =
    appConfig?.preAuthConfig?.brandColor || appConfig?.company?.baseDetails?.brandColor || DEFAULT_BRAND_COLOR;
  return { applicationName, brandPrimaryColor };
}

export function useAppLocale() {
  const config = useAppConfig();
  return getLanguageFromConfig(config);
}
